import type { ReactElement } from 'react';
import type { WeekDay, WeekTotals } from '@/lib/workouts';
import { useActiveSeason } from '@/lib/season';

/**
 * Card "TU SEMANA" del dashboard (M2.12).
 *
 * Reemplaza a la `WeekTotalsCard` minimalista por un panel ancho con:
 *  - Stats: nº sesiones, horas totales (planificadas vs completadas), TSS total.
 *  - Barra de progreso vs target (target = `weeklyHoursBaseline` del plan
 *    activo si existe, else 8h por defecto).
 *  - Mini-bars por día (sparkline con 7 barras, altura proporcional a horas
 *    de cada día). Las barras se colorean según si ese día está completado
 *    (teal) o solo planificado (slate-300).
 *
 * Decisiones:
 *  - "TSS total" se aproxima sumando `tssComputed`/`tssEstimated` de los
 *    actuals del array `days[*].workouts[*].actualWorkout` SI están — la API
 *    actual los expone ahí. Si no, mostramos "—".
 *  - El target horas se lee de `useActiveSeason` (sin disparar si no hay
 *    plan); fallback a 8h.
 */
export function WeekSummaryCard({
  totals,
  days,
}: {
  totals: WeekTotals;
  days: WeekDay[];
}): ReactElement {
  const seasonQuery = useActiveSeason();
  const targetHours = seasonQuery.data?.weeklyHoursBaseline ?? 8;
  const pct = targetHours > 0
    ? Math.min(100, Math.round((totals.plannedHours / targetHours) * 100))
    : 0;

  // Suma de TSS de los actuals (si llegan dentro de los days).
  const tssTotal = sumWeeklyTss(days);

  // Horas por día para el sparkline (planificadas + huérfanos).
  const dailyHours = days.map((d) => {
    let mins = 0;
    let completed = false;
    for (const w of d.workouts) {
      mins += w.durationPlannedMin;
      if (w.status === 'completed' || w.status === 'partial') completed = true;
    }
    for (const a of d.unplannedActuals ?? []) {
      mins += a.durationMin;
      completed = true;
    }
    return { dayKey: d.dayKey, hours: mins / 60, completed };
  });
  const maxHours = dailyHours.reduce((m, x) => Math.max(m, x.hours), 1);

  return (
    <article
      aria-label="Resumen de tu semana"
      className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm"
    >
      <header className="flex items-baseline justify-between">
        <h2 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
          Tu semana
        </h2>
        <p className="text-xs text-slate-500">
          objetivo {targetHours} h
        </p>
      </header>

      <div className="mt-3 flex flex-wrap items-baseline gap-x-6 gap-y-2">
        <Metric
          big={`${totals.completedCount}/${totals.plannedCount}`}
          small="sesiones"
        />
        <Metric
          big={formatHours(totals.completedHours)}
          small={`/ ${formatHours(totals.plannedHours)} planif.`}
        />
        <Metric
          big={tssTotal === null ? '—' : Math.round(tssTotal).toString()}
          small="TSS"
        />
      </div>

      <div className="mt-4">
        <div
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={pct}
          aria-label={`Progreso vs objetivo: ${pct}%`}
          className="h-2 w-full overflow-hidden rounded-full bg-slate-200"
        >
          <div
            className="h-full bg-teal-600 transition-all"
            style={{ width: `${pct}%` }}
          />
        </div>
        <p className="mt-1 text-xs text-slate-500">
          {formatHours(totals.plannedHours)} de {targetHours} h objetivo ({pct}%)
        </p>
      </div>

      <DailySparkline daily={dailyHours} max={maxHours} />
    </article>
  );
}

// ---------------------------------------------------------------------------
// Sub-componentes
// ---------------------------------------------------------------------------

function Metric({ big, small }: { big: string; small: string }): ReactElement {
  return (
    <div className="flex items-baseline gap-1.5">
      <span className="text-2xl font-bold tabular-nums text-slate-900">{big}</span>
      <span className="text-xs text-slate-600">{small}</span>
    </div>
  );
}

function DailySparkline({
  daily,
  max,
}: {
  daily: Array<{ dayKey: string; hours: number; completed: boolean }>;
  max: number;
}): ReactElement {
  const labels: Record<string, string> = {
    mon: 'L',
    tue: 'M',
    wed: 'X',
    thu: 'J',
    fri: 'V',
    sat: 'S',
    sun: 'D',
  };
  return (
    <div className="mt-4">
      <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
        Por día
      </p>
      <ul aria-label="Horas por día de la semana" className="flex h-16 items-end gap-1.5">
        {daily.map((d, i) => {
          const heightPct = d.hours > 0 ? Math.max(8, Math.round((d.hours / max) * 100)) : 4;
          const color = d.hours === 0
            ? 'bg-slate-100'
            : d.completed
              ? 'bg-teal-600'
              : 'bg-slate-300';
          return (
            <li
              key={`${d.dayKey}-${i}`}
              className="flex flex-1 flex-col items-center gap-1"
            >
              <div
                aria-label={`${labels[d.dayKey] ?? d.dayKey}: ${formatHours(d.hours)}`}
                title={`${labels[d.dayKey] ?? d.dayKey}: ${formatHours(d.hours)}`}
                style={{ height: `${heightPct}%` }}
                className={`w-full rounded-sm ${color}`}
              />
              <span className="text-[10px] font-medium text-slate-500">
                {labels[d.dayKey] ?? d.dayKey}
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatHours(h: number): string {
  if (!Number.isFinite(h) || h <= 0) return '0 h';
  if (Number.isInteger(h)) return `${h} h`;
  return `${h.toFixed(1)} h`;
}

/**
 * Suma TSS de la semana usando los actuals embebidos en planned.actualWorkout
 * (M2.6 — el schema PlannedWorkout puede traer `actualWorkout` con métricas).
 * Si ninguno tiene TSS válido, devuelve null para mostrar "—" en la UI.
 */
function sumWeeklyTss(days: WeekDay[]): number | null {
  let sum = 0;
  let found = false;
  for (const d of days) {
    for (const w of d.workouts) {
      // PlannedWorkout puede o no traer `actualWorkout` según el schema FE;
      // accedemos defensivamente sin asumir shape exacto.
      const actual = (w as unknown as { actualWorkout?: { tssEstimated?: number | null } | null }).actualWorkout;
      const tss = actual?.tssEstimated;
      if (typeof tss === 'number' && tss > 0) {
        sum += tss;
        found = true;
      }
    }
    for (const a of d.unplannedActuals ?? []) {
      const tss = a.tssEstimated;
      if (typeof tss === 'number' && tss > 0) {
        sum += tss;
        found = true;
      }
    }
  }
  return found ? sum : null;
}
